import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import useSWR from 'swr';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import is from 'date-fns/locale/is';
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    RadioGroup,
    Radio,
    FormLabel,
    Skeleton,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { AddAlert } from '@mui/icons-material';

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SingleImageUpload from 'components/SingleImageUpload/SingleImageUpload';
import SnackbarCustom from 'components/Snackbar/Snackbar.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import SendUserNotificationModal from 'views/users/SendUserNotificationModal/SendUserNotificationModal';
import { regionApi, appUserApi, fetcher } from 'lib/api';

registerLocale('is', is);

const style = {
    pageContainer: {
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '0 20px',
    },
    sectionHeading: {
        marginLeft: '10px',
        marginBottom: '5px',
        fontWeight: 'bold',
        fontStyle: 'italic',
        color: '#de2c49',
    },
    inputContainer: {
        marginBottom: '24px',
    },
    sparaField: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            fontSize: '14px',
            height: '40px',
            '& fieldset': { borderColor: '#d2d2d2' },
            '&:hover fieldset': { borderColor: '#aaa' },
            '&.Mui-focused fieldset': { borderColor: '#de2c49', borderWidth: '1px' },
        },
        '& .MuiInputLabel-root': {
            fontSize: '14px',
            color: '#AAAAAA',
            '&.Mui-focused': { color: '#de2c49' },
        },
        '& .MuiSelect-select': { fontSize: '14px' },
        '& .react-datepicker-wrapper': { width: '100%' },
        '& .react-datepicker__input-container': { width: '100%' },
        '& .react-datepicker__day--selected, & .react-datepicker__day--keyboard-selected': {
            backgroundColor: '#de2c49',
            borderRadius: '50%',
        },
        '& .react-datepicker__day--selected:hover, & .react-datepicker__day--keyboard-selected:hover': {
            backgroundColor: '#b52038',
        },
        '& .react-datepicker__header': {
            backgroundColor: '#fff',
            borderBottom: '1px solid #f0f0f0',
        },
    },
    errorText: {
        fontSize: '12px',
        color: '#de2c49',
        margin: '4px 0 0 0',
    },
    infoBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginBottom: '20px',
    },
    infoLabel: {
        fontSize: '11px',
        color: '#AAAAAA',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontWeight: '400',
        margin: 0,
    },
    loginMethodBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '500',
        width: 'fit-content',
    },
};

const useStyles = createUseStyles(style);

const emptyForm = () => ({
    id: '',
    email: '',
    phone: '',
    kennitala: '',
    name: '',
    address: '',
    position: '',
    date_of_birth: null,
    torgid_region_id: '',
    postalcode_number: '',
    postalcode_city: '',
    gender: '',
    language: 'IS',
    subscribed: true,
    profile_picture: '',
    type: 'App_User',
});

const formFromUser = (u) => ({
    id: u.id || '',
    email: u.email || '',
    phone: u.phone || '',
    kennitala: u.kennitala || '',
    name: u.name || '',
    address: u.address || '',
    position: u.position || '',
    date_of_birth: u.date_of_birth || null,
    torgid_region_id: u.torgid_region_id || '',
    postalcode_number: u.torgid_region?.postal_code || '',
    postalcode_city: u.torgid_region?.city || '',
    gender: u.gender || '',
    language: (u.language || 'IS').toUpperCase(),
    subscribed: u.subscribed ?? true,
    profile_picture: u.profile_picture || '',
    type: u.type || 'App_User',
});

const AddUser = ({ t }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { id } = useParams();
    const isUpdating = id !== 'new';

    const { data: userData, isLoading: userLoading } = useSWR(
        isUpdating ? appUserApi.get(id) : null,
        fetcher,
        { revalidateOnFocus: false }
    );

    const existingUser = userData?.results || {};

    const { data: regionsData } = useSWR(regionApi.list, fetcher, { revalidateOnFocus: false });

    const [form, setForm] = useState(emptyForm());
    const [formReady, setFormReady] = useState(!isUpdating);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
    const [submitting, setSubmitting] = useState(false);
    const [notificationModalOpen, setNotificationModalOpen] = useState(false);

    useEffect(() => {
        if (userData?.results && !formReady) {
            setForm(formFromUser(userData.results));
            setFormReady(true);
        }
    }, [userData, formReady]);

    const change = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const handleChangeRegion = (region) => {
        if (!region) {
            setForm((prev) => ({
                ...prev,
                torgid_region_id: '',
                postalcode_number: '',
                postalcode_city: '',
            }));
            return;
        }
        setForm((prev) => ({
            ...prev,
            torgid_region_id: region.id,
            postalcode_number: region.postal_code,
            postalcode_city: region.city,
        }));
    };

    const validate = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (form.email && !emailRegex.test(form.email)) {
            newErrors.email = t('validation.email_invalid');
        }
        if (!form.email && !form.phone) {
            newErrors.email = t('validation.email_or_phone_required');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = async () => {
        if (!validate()) return;
        setSubmitting(true);
        try {
            await fetcher(
                isUpdating ? appUserApi.update(form.id) : appUserApi.create,
                isUpdating ? 'PUT' : 'POST',
                { ...form }
            );
            setNotification({ open: true, message: t('saved_successfully'), type: 'success' });
            setTimeout(() => navigate('/app-users'), 1500);
        } catch (error) {
            const res = error?.response?.data;
            const detail = res?.results ? res.results[Object.keys(res.results)[0]] : '';
            const msg =
                (res?.message || t('common.error_occurred')) + (detail ? '. ' + detail : '');
            setNotification({ open: true, message: msg, type: 'warning' });
        } finally {
            setSubmitting(false);
        }
    };

    const getLoginMethodDisplay = (method) => {
        switch (method) {
            case 'sim':
                return {
                    label: 'Rafræn skilríki (SIM)',
                    color: '#1976d2',
                    bg: '#e3f2fd',
                    icon: '📱',
                };
            case 'app':
                return { label: 'Auðkennisapp', color: '#6a1b9a', bg: '#f3e5f5', icon: '🔐' };
            case 'sms':
                return { label: 'SMS', color: '#388e3c', bg: '#e8f5e9', icon: '💬' };
            default:
                return method ? { label: method, color: '#666', bg: '#f5f5f5', icon: '🔑' } : null;
        }
    };

    const loginMethodDisplay = isUpdating
        ? getLoginMethodDisplay(existingUser.last_login_method)
        : null;

    const regions = regionsData?.results || [];
    const regionOptions = regions.map((option) => {
        const firstLetter = option.torgid_region_name?.name?.toUpperCase() || '';
        return { firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter, ...option };
    });

    if (isUpdating && userLoading) {
        return (
            <div className={classes.pageContainer}>
                <GridContainer>
                    <GridItem xs={12}>
                        <Skeleton variant="text" width={300} height={40} sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={400} />
                    </GridItem>
                </GridContainer>
            </div>
        );
    }

    return (
        <div className={classes.pageContainer}>
            <GridContainer>
                <GridItem xs={12}>
                    <SparaTitle
                        title={
                            isUpdating
                                ? t('app_user_add.update_profile')
                                : t('app_user_add.build_profile')
                        }
                        rightComponent={
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                {isUpdating && (
                                    <SparaButton
                                        color="secondary"
                                        variant="outlined"
                                        onClick={() => setNotificationModalOpen(true)}
                                        disabled={!existingUser?.has_device_token}
                                        title={
                                            existingUser?.has_device_token
                                                ? t('send_notification')
                                                : t('user_notification.no_device_token')
                                        }
                                    >
                                        {t('send_notification')}
                                    </SparaButton>
                                )}
                                <SparaButton color="primary" onClick={onSubmit} disabled={submitting}>
                                    {isUpdating ? t('save') : t('finish')}
                                </SparaButton>
                            </div>
                        }
                    />

                    <h3 className={classes.sectionHeading}>{t('app_user_add.about')}</h3>
                    <Card style={{ margin: '0 0 16px 0' }}>
                        <CardBody>
                            <GridContainer>
                                <GridItem xs={12} sm={4} className={classes.inputContainer}>
                                    <SingleImageUpload
                                        image={form.profile_picture}
                                        onUpload={(data) => change('profile_picture', data)}
                                        onDelete={() => change('profile_picture', '')}
                                        onError={() => change('profile_picture', '')}
                                        cropShape="round"
                                        aspectRatio={1}
                                    />
                                </GridItem>

                                <GridItem xs={12} sm={8}>
                                    {isUpdating && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: '24px',
                                                flexWrap: 'wrap',
                                                marginBottom: '16px',
                                            }}
                                        >
                                            {loginMethodDisplay && (
                                                <div
                                                    className={classes.infoBlock}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <p className={classes.infoLabel}>
                                                        {t('app_user.last_login_method')}
                                                    </p>
                                                    <div
                                                        className={classes.loginMethodBadge}
                                                        style={{
                                                            backgroundColor: loginMethodDisplay.bg,
                                                            color: loginMethodDisplay.color,
                                                            border: `1px solid ${loginMethodDisplay.color}33`,
                                                        }}
                                                    >
                                                        <span>{loginMethodDisplay.icon}</span>
                                                        <span>{loginMethodDisplay.label}</span>
                                                    </div>
                                                </div>
                                            )}
                                            <div
                                                className={classes.infoBlock}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <p className={classes.infoLabel}>
                                                    {t('app_user.app_version')}
                                                </p>
                                                <div
                                                    className={classes.loginMethodBadge}
                                                    style={{
                                                        backgroundColor: '#f5f5f5',
                                                        color: '#555',
                                                        border: '1px solid #ddd',
                                                    }}
                                                >
                                                    <span>📦</span>
                                                    <span>{existingUser.app_version || '—'}</span>
                                                </div>
                                            </div>
                                            {existingUser.device_os && (
                                                <div
                                                    className={classes.infoBlock}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <p className={classes.infoLabel}>
                                                        {t('app_user.device_os')}
                                                    </p>
                                                    <div
                                                        className={classes.loginMethodBadge}
                                                        style={{
                                                            backgroundColor: existingUser.device_os
                                                                ?.toLowerCase()
                                                                .includes('ios')
                                                                ? '#e3f2fd'
                                                                : '#e8f5e9',
                                                            color: existingUser.device_os
                                                                ?.toLowerCase()
                                                                .includes('ios')
                                                                ? '#1976d2'
                                                                : '#388e3c',
                                                            border: `1px solid ${
                                                                existingUser.device_os
                                                                    ?.toLowerCase()
                                                                    .includes('ios')
                                                                    ? '#1976d233'
                                                                    : '#388e3c33'
                                                            }`,
                                                        }}
                                                    >
                                                        <span>
                                                            {existingUser.device_os
                                                                ?.toLowerCase()
                                                                .includes('ios')
                                                                ? '🍎'
                                                                : '🤖'}
                                                        </span>
                                                        <span>
                                                            {existingUser.device_os
                                                                ?.toLowerCase()
                                                                .includes('ios')
                                                                ? 'iOS'
                                                                : 'Android'}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                            <div
                                                className={classes.infoBlock}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <p className={classes.infoLabel}>
                                                    {t('app_user.device_token')}
                                                </p>
                                                <div
                                                    className={classes.loginMethodBadge}
                                                    style={{
                                                        backgroundColor: existingUser.has_device_token
                                                            ? '#e8f5e9'
                                                            : '#fafafa',
                                                        color: existingUser.has_device_token
                                                            ? '#388e3c'
                                                            : '#999',
                                                        border: `1px solid ${
                                                            existingUser.has_device_token
                                                                ? '#388e3c33'
                                                                : '#ddd'
                                                        }`,
                                                    }}
                                                >
                                                    <span>
                                                        {existingUser.has_device_token
                                                            ? '🔔'
                                                            : '🔕'}
                                                    </span>
                                                    <span>
                                                        {existingUser.has_device_token
                                                            ? t('yes')
                                                            : t('no')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <GridContainer>
                                        <GridItem xs={12} sm={6} className={classes.inputContainer}>
                                            <div className={classes.sparaField}>
                                                <TextField
                                                    label={t('full_name')}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    value={form.name}
                                                    onChange={(e) => change('name', e.target.value)}
                                                />
                                            </div>
                                        </GridItem>

                                        <GridItem xs={12} sm={6} className={classes.inputContainer}>
                                            <div className={classes.sparaField}>
                                                <TextField
                                                    label={t('kennitala')}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    value={form.kennitala}
                                                    onChange={(e) =>
                                                        change('kennitala', e.target.value)
                                                    }
                                                />
                                            </div>
                                        </GridItem>

                                        <GridItem xs={12} sm={6} className={classes.inputContainer}>
                                            <div className={classes.sparaField}>
                                                <TextField
                                                    label={t('email')}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    type="email"
                                                    value={form.email}
                                                    onChange={(e) =>
                                                        change('email', e.target.value)
                                                    }
                                                    error={!!errors.email}
                                                />
                                            </div>
                                            {errors.email && (
                                                <p className={classes.errorText}>{errors.email}</p>
                                            )}
                                        </GridItem>

                                        <GridItem xs={12} sm={6} className={classes.inputContainer}>
                                            <div className={classes.sparaField}>
                                                <TextField
                                                    label={t('phone_number')}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    value={form.phone}
                                                    onChange={(e) =>
                                                        change('phone', e.target.value)
                                                    }
                                                />
                                            </div>
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>

                                <GridItem xs={12} sm={4} className={classes.inputContainer}>
                                    <div className={classes.sparaField}>
                                        <TextField
                                            label={t('position')}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={form.position}
                                            onChange={(e) => change('position', e.target.value)}
                                        />
                                    </div>
                                </GridItem>

                                <GridItem xs={12} sm={4} className={classes.inputContainer}>
                                    <div className={classes.sparaField}>
                                        <DatePicker
                                            selected={
                                                form.date_of_birth
                                                    ? new Date(form.date_of_birth)
                                                    : null
                                            }
                                            onChange={(date) =>
                                                change(
                                                    'date_of_birth',
                                                    date ? date.toISOString().split('T')[0] : null
                                                )
                                            }
                                            dateFormat="dd.MM.yyyy"
                                            showYearDropdown
                                            showMonthDropdown
                                            dropdownMode="select"
                                            maxDate={new Date()}
                                            locale="is"
                                            yearDropdownItemNumber={80}
                                            scrollableYearDropdown
                                            customInput={
                                                <TextField
                                                    label={t('date_of_birth')}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    InputProps={{ readOnly: true }}
                                                />
                                            }
                                        />
                                    </div>
                                </GridItem>

                                <GridItem xs={12} sm={4} className={classes.inputContainer}>
                                    <div className={classes.sparaField}>
                                        <Autocomplete
                                            id="region"
                                            onChange={(event, newValue) =>
                                                handleChangeRegion(newValue)
                                            }
                                            value={
                                                form.torgid_region_id && form.postalcode_number
                                                    ? {
                                                          id: form.torgid_region_id,
                                                          postal_code: form.postalcode_number,
                                                          city: form.postalcode_city,
                                                      }
                                                    : null
                                            }
                                            options={regionOptions.sort(
                                                (a, b) =>
                                                    -b.firstLetter.localeCompare(a.firstLetter)
                                            )}
                                            groupBy={(option) => option.firstLetter}
                                            getOptionLabel={(option) =>
                                                option.postal_code + ' ' + option.city
                                            }
                                            isOptionEqualToValue={(option, value) =>
                                                option.id === value.id
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('select_region')}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </div>
                                </GridItem>

                                <GridItem xs={12} sm={4} className={classes.inputContainer}>
                                    <div className={classes.sparaField}>
                                        <FormControl fullWidth size="small" variant="outlined">
                                            <InputLabel>{t('choose_language')}</InputLabel>
                                            <Select
                                                value={form.language}
                                                onChange={(e) => change('language', e.target.value)}
                                                label={t('choose_language')}
                                            >
                                                <MenuItem value="IS">{t('icelandic')}</MenuItem>
                                                <MenuItem value="EN">{t('english')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>
                                </GridItem>

                                <GridItem xs={12} sm={4} className={classes.inputContainer}>
                                    <FormControl>
                                        <FormLabel style={{ fontSize: '13px', color: '#AAAAAA' }}>
                                            {t('gender')}
                                        </FormLabel>
                                        <RadioGroup
                                            row
                                            value={form.gender}
                                            onChange={(e) => change('gender', e.target.value)}
                                        >
                                            <FormControlLabel
                                                value="male"
                                                control={
                                                    <Radio
                                                        size="small"
                                                        sx={{
                                                            color: '#de2c49',
                                                            '&.Mui-checked': { color: '#de2c49' },
                                                        }}
                                                    />
                                                }
                                                label={t('male')}
                                            />
                                            <FormControlLabel
                                                value="female"
                                                control={
                                                    <Radio
                                                        size="small"
                                                        sx={{
                                                            color: '#de2c49',
                                                            '&.Mui-checked': { color: '#de2c49' },
                                                        }}
                                                    />
                                                }
                                                label={t('female')}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </GridItem>

                                <GridItem xs={12} sm={4} className={classes.inputContainer}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={form.subscribed}
                                                onChange={(e) =>
                                                    change('subscribed', e.target.checked)
                                                }
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: '#de2c49',
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: '#de2c49',
                                                    },
                                                }}
                                            />
                                        }
                                        label={t('subscribe')}
                                    />
                                </GridItem>
                            </GridContainer>
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>

            <SendUserNotificationModal
                open={notificationModalOpen}
                onClose={() => setNotificationModalOpen(false)}
                userId={id}
                t={t}
                onSent={() =>
                    setNotification({
                        open: true,
                        message: t('user_notification.send_success'),
                        type: 'success',
                    })
                }
            />

            <SnackbarCustom
                place="br"
                icon={AddAlert}
                color={notification.type === 'success' ? 'success' : 'warning'}
                message={notification.message}
                open={notification.open}
                closeNotification={() => setNotification((prev) => ({ ...prev, open: false }))}
            />
        </div>
    );
};

export default withTranslation('', { withRef: true })(AddUser);
